import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, unref as _unref, resolveComponent as _resolveComponent, createBlock as _createBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["data-sub-groups-depth"]
const _hoisted_2 = {
  key: 0,
  class: "absolute left-[calc((1rem+1px)*-1)] top-1/2 h-px w-[calc(1rem+1px)] bg-grey-700"
}
const _hoisted_3 = { class: "mb-6 flex items-center gap-2" }
const _hoisted_4 = { class: "grow xs:flex xs:flex-row-reverse xs:justify-between xs:gap-2" }
const _hoisted_5 = { class: "mb-4 ml-auto mr-0 flex flex-wrap justify-end gap-2 xs:mb-0" }
const _hoisted_6 = { class: "mr-2 text-xs font-bold uppercase" }
const _hoisted_7 = { class: "ml-2 text-xs font-bold uppercase" }
const _hoisted_8 = {
  key: 2,
  class: "relative ml-4 mt-3 flex flex-wrap items-center justify-center gap-2 rounded-md border-2 border-dashed border-primary-tint py-1 pl-4 pr-1 last:mb-3"
}

import type { Condition, ConditionEditorProperty, GroupCondition } from "@/lib/helpers/condition";
import { computed, watchEffect, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import VueDraggable from "vuedraggable";

import ConditionProperty from "./property.vue";
import ConditionGroup from "./group.vue";

import ICON_PLUS from "@/lib/icons/plus.json";
import ICON_TRASH from "@/lib/icons/trash.json";
import ICON_DRAG_HANDLE from "@/lib/icons/burger.json";

type DraggableChangedEvent<T> = {
  added?: { newIndex: number; element: T };
  removed?: { oldIndex: number; element: T };
  moved?: { newIndex: number; oldIndex: number; element: T };
};

export default /*@__PURE__*/_defineComponent({
  __name: 'group',
  props: {
    groupLevel: { default: 0 },
    modelValue: {},
    properties: { default: undefined },
    maxDepth: {},
    showHandle: { type: Boolean }
  },
  emits: ["update:modelValue", "delete"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const emit = __emit;

const props = __props;

const orConditionEnabled = computed(() => (props.modelValue.group === "OR" ? true : false));

const children = computed(() => props.modelValue?.children ?? []);

/* Générer un ID unique temporaire pour chaque étément du groupe.
 * Ça sert de key pour le template. Ces ID sont perdus lors de la
 * sauvegarde puisque le backend désérialise le JSON reçu. */
watchEffect(() => addIds(children.value));
function addIds(children: (Condition & { _id?: string })[]) {
  for (const child of children) {
    if (!child._id) {
      child._id = Date.now().toString() + Math.random().toString();
    }
  }
}

/* Calcul de la profondeur des sous groupes.
 * Si le groupe actuel contient un sous-groupe qui contient à son tour un sous-groupe,
 * la profondeur est de 2
 */
const subGroupsDepth = computed(() => {
  function depth(group: GroupCondition): number {
    if (!group.children) return 0;
    var subGroups = group.children.filter((g): g is GroupCondition => "group" in g);
    if (subGroups.length === 0) return 0;

    return Math.max(...subGroups.map(depth)) + 1;
  }

  return depth(props.modelValue);
});

const draggableGroupConfig = {
  name: "condition-group",
  put(to: unknown, from: unknown, element: HTMLElement) {
    // Ne pas accepter un groupe si notre profondeur maximale n'est pas suffisante
    // pour la profondeur du groupe qui s'en vient
    var groupElement: HTMLElement | null = element.querySelector("[data-sub-groups-depth]");
    if (!!groupElement && props.maxDepth <= Number(groupElement.dataset.subGroupsDepth)) {
      return false;
    }
    return true;
  }
};

function handleDraggableChange({ added, removed, moved }: DraggableChangedEvent<Condition>) {
  if (moved) {
    const newChildren = [...props.modelValue.children];
    newChildren.splice(moved.oldIndex, 1);
    newChildren.splice(moved.newIndex, 0, moved.element);
    onUpdate({ children: newChildren });
  }

  if (added) {
    const newChildren = [...props.modelValue.children];
    newChildren.splice(added.newIndex, 0, added.element);
    onUpdate({ children: newChildren });
  }

  if (removed) {
    nextTick(() => {
      const newChildren = [...props.modelValue.children];
      newChildren.splice(removed.oldIndex, 1);
      onUpdate({ children: newChildren });
    });
  }
}

function onUpdate(newValues: Partial<GroupCondition>) {
  emit("update:modelValue", { ...props.modelValue, ...newValues });
}

function updateAt(index: number, value: Condition) {
  const newChildren = [...children.value];
  newChildren.splice(index, 1, value);

  onUpdate({ children: newChildren });
}

function addGroupAt(index: number) {
  const newChildren = [...children.value];
  newChildren.splice(index, 0, { group: "AND", children: [] });

  onUpdate({ children: newChildren });
}

/* C'est un peu un hack:
 * En temps normal, comme le composant qui a cette ref est dans une boucle,
 * on s'attendrait à avoir une array dans la ref.
 * Par contre, c'est vuedraggable qui gère la liste, et il ne crée pas d'array.
 * C'est donc toujours le dernier élément qui est dans la ref.
 * C'est toutefois parfait pour notre besoin d'ouvrir le popup d'édition après
 * l'ajout d'une propriété, puisque la nouvelle propriété est toujours à la fin
 * de la liste.
 * */
const lastProp = ref<{ openModal?: () => void }>();

function addPropertyAt(index: number) {
  const newChildren = [...children.value];
  newChildren.splice(index, 0, { prop: null });

  onUpdate({ children: newChildren });

  nextTick(() => {
    const editor = lastProp.value;
    if (editor?.openModal) {
      editor.openModal();
    }
  });
}

function removeAt(index: number) {
  const newChildren = [...children.value];
  newChildren.splice(index, 1);

  onUpdate({ children: newChildren });
}

function toggleGroupType() {
  const newCondition = props.modelValue.group === "OR" ? "AND" : "OR";
  onUpdate({ group: newCondition });
}

return (_ctx: any,_cache: any) => {
  const _component_PfIcon = _resolveComponent("PfIcon")!
  const _component_PfButtonAction = _resolveComponent("PfButtonAction")!
  const _component_UiSwitch = _resolveComponent("UiSwitch")!

  return (_openBlock(), _createElementBlock("div", {
    "data-sub-groups-depth": subGroupsDepth.value,
    class: _normalizeClass(["relative rounded border border-t-2 border-primary-main py-2 pl-6 pr-4", [{ 'group-one': props.groupLevel === 1 }, { 'group-two': props.groupLevel > 1 }]])
  }, [
    (props.groupLevel > 0)
      ? (_openBlock(), _createElementBlock("span", _hoisted_2))
      : _createCommentVNode("", true),
    (props.groupLevel > 0)
      ? (_openBlock(), _createElementBlock("span", {
          key: 1,
          class: _normalizeClass(["absolute bottom-1/2 left-[calc((1rem+1px)*-1)] h-[calc(50%+1.5rem)] w-px bg-grey-700", 
        props.groupLevel === 1
          ? 'group-one-last:bottom-1/2 group-one-last:h-[calc(50%+1.5rem)]'
          : 'group-two-last:bottom-1/2 group-two-last:h-[calc(50%+1.5rem)]'
      ])
        }, null, 2))
      : _createCommentVNode("", true),
    _createVNode(_unref(VueDraggable), {
      handle: ".handle",
      group: draggableGroupConfig,
      "ghost-class": "opacity-50",
      animation: "150",
      "model-value": children.value,
      "item-key": "_id",
      onChange: handleDraggableChange
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          (__props.showHandle)
            ? (_openBlock(), _createBlock(_component_PfIcon, {
                key: 0,
                class: "handle cursor-move opacity-60",
                size: "sm",
                icon: _unref(ICON_DRAG_HANDLE)
              }, null, 8, ["icon"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_PfButtonAction, {
                "btn-style": props.groupLevel > 0 ? 'outline' : 'primary',
                icon: _unref(ICON_PLUS),
                size: "sm",
                "has-icon-left": "",
                label: _unref(t)('add-prop'),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (addPropertyAt(children.value.length)))
              }, null, 8, ["btn-style", "icon", "label"]),
              (props.maxDepth > 0)
                ? (_openBlock(), _createBlock(_component_PfButtonAction, {
                    key: 0,
                    "btn-style": props.groupLevel > 0 ? 'outline' : 'primary',
                    icon: _unref(ICON_PLUS),
                    size: "sm",
                    "has-icon-left": "",
                    label: _unref(t)('add-group'),
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (addGroupAt(children.value.length)))
                  }, null, 8, ["btn-style", "icon", "label"]))
                : _createCommentVNode("", true),
              (props.groupLevel > 0)
                ? (_openBlock(), _createBlock(_component_PfButtonAction, {
                    key: 1,
                    "btn-style": "outline",
                    size: "sm",
                    "is-icon-only": "",
                    icon: _unref(ICON_TRASH),
                    "aria-labelledby": _unref(t)('delete-group'),
                    onClick: _cache[2] || (_cache[2] = () => emit('delete'))
                  }, null, 8, ["icon", "aria-labelledby"]))
                : _createCommentVNode("", true)
            ]),
            _createVNode(_component_UiSwitch, {
              "model-value": orConditionEnabled.value,
              label: _unref(t)('condition-type'),
              "onUpdate:modelValue": toggleGroupType
            }, {
              left: _withCtx(() => [
                _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)("group-and")), 1)
              ]),
              right: _withCtx(() => [
                _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)("group-or")), 1)
              ]),
              _: 1
            }, 8, ["model-value", "label"])
          ])
        ])
      ]),
      item: _withCtx(({ element: child, index }) => [
        _createElementVNode("div", null, [
          ('group' in child)
            ? (_openBlock(), _createBlock(ConditionGroup, {
                key: 0,
                class: "ml-4 mt-5",
                "show-handle": "",
                "group-level": props.groupLevel + 1,
                properties: props.properties,
                "model-value": child,
                "max-depth": props.maxDepth - 1,
                "onUpdate:modelValue": (x) => updateAt(index, x),
                onDelete: () => removeAt(index)
              }, null, 8, ["group-level", "properties", "model-value", "max-depth", "onUpdate:modelValue", "onDelete"]))
            : (_openBlock(), _createBlock(ConditionProperty, {
                key: 1,
                ref_key: "lastProp",
                ref: lastProp,
                "show-handle": "",
                properties: props.properties,
                "model-value": child,
                "onUpdate:modelValue": (x) => updateAt(index, x),
                onDelete: () => removeAt(index)
              }, null, 8, ["properties", "model-value", "onUpdate:modelValue", "onDelete"]))
        ])
      ]),
      footer: _withCtx(() => [...(_cache[5] || (_cache[5] = []))]),
      _: 1
    }, 8, ["model-value"]),
    (props.groupLevel === 0 || children.value.length <= 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createVNode(_component_PfButtonAction, {
            "btn-style": "outline",
            icon: _unref(ICON_PLUS),
            size: "sm",
            "has-icon-left": "",
            label: _unref(t)('add-prop'),
            onClick: _cache[3] || (_cache[3] = ($event: any) => (addPropertyAt(children.value.length)))
          }, null, 8, ["icon", "label"]),
          (props.maxDepth > 0)
            ? (_openBlock(), _createBlock(_component_PfButtonAction, {
                key: 0,
                "btn-style": "outline",
                icon: _unref(ICON_PLUS),
                size: "sm",
                "has-icon-left": "",
                label: _unref(t)('add-group'),
                onClick: _cache[4] || (_cache[4] = ($event: any) => (addGroupAt(children.value.length)))
              }, null, 8, ["icon", "label"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 10, _hoisted_1))
}
}

})